/**
 * Pure helpers for the Timeline + Page Detail screens. No JSX, no side effects.
 *
 * A few values the design shows are not carried by the vault API at the sweep
 * level (per-sweep browser identity, AI topic tags). Where a real field is
 * missing we derive a *stable* placeholder from data we do have (deviceId /
 * sweep id) so the same device always reads the same way — never a random value.
 */
import type { Sweep } from '@vault/shared';
import type { BrowserName } from '@vault/ui';

const WD = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'] as const;
const MO = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'] as const;
const WD_INITIAL = ['S', 'M', 'T', 'W', 'T', 'F', 'S'] as const;

/** Rough per-tab RAM footprint used only for the "RAM freed" estimate. */
const RAM_PER_TAB_MB = 55;

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** FNV-1a — deterministic, cheap, good enough for stable placeholder picks. */
function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export function dayKey(ts: number): string {
  const d = new Date(ts);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/** e.g. "TODAY · TUE MAR 17", "YESTERDAY · MON MAR 16", "SAT MAR 14". */
export function dayEyebrow(ts: number): string {
  const d = new Date(ts);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const stamp = `${WD[d.getDay()] ?? ''} ${MO[d.getMonth()] ?? ''} ${d.getDate()}`;
  if (sameDay(d, today)) return `TODAY · ${stamp}`;
  if (sameDay(d, yesterday)) return `YESTERDAY · ${stamp}`;
  return stamp;
}

export function formatTime(ts: number): string {
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** e.g. "Mar 15, 2026". */
export function formatDate(ts: number): string {
  const d = new Date(ts);
  const mon = MO[d.getMonth()] ?? '';
  const title = mon.charAt(0) + mon.slice(1).toLowerCase();
  return `${title} ${d.getDate()}, ${d.getFullYear()}`;
}

/** e.g. "Mar 15 · 09:41". */
export function formatDateTime(ts: number): string {
  const d = new Date(ts);
  const mon = MO[d.getMonth()] ?? '';
  const title = mon.charAt(0) + mon.slice(1).toLowerCase();
  return `${title} ${d.getDate()} · ${formatTime(ts)}`;
}

export function domainOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url.replace(/^https?:\/\//, '').split('/')[0] ?? url;
  }
}

const BROWSERS: readonly BrowserName[] = ['chrome', 'edge', 'yandex', 'comet', 'firefox', 'safari'];

/** Stable per-device browser identity (the API omits it; a device maps 1:1). */
export function deriveBrowser(seed: string | null): BrowserName {
  return BROWSERS[hash(seed ?? 'chrome') % BROWSERS.length] ?? 'chrome';
}

/** Deterministic placeholder initials for a collapsed card (design DNA: favicons
 *  are always site-initial placeholders, never real logos). */
export function seedLetters(seed: string, count: number): string[] {
  const out: string[] = [];
  let h = hash(seed) || 7;
  for (let i = 0; i < count; i++) {
    out.push(String.fromCharCode(65 + (h % 26)));
    h = Math.floor(h / 3) + 31 + i * 13;
  }
  return out;
}

export function ramFreed(tabs: number): string {
  const mb = tabs * RAM_PER_TAB_MB;
  if (mb < 1024) return `≈ ${Math.round(mb)} MB`;
  const gb = mb / 1024;
  if (gb < 1024) return `≈ ${gb.toFixed(1)} GB`;
  return `≈ ${(gb / 1024).toFixed(1)} TB`;
}

export function fallbackTitle(s: Sweep): string {
  if (s.title && s.title.trim()) return s.title.trim();
  if (s.kind === 'import') return 'Imported tabs';
  const h = new Date(s.clientCreatedAt).getHours();
  const part = h < 5 ? 'Late night' : h < 12 ? 'Morning' : h < 17 ? 'Afternoon' : h < 22 ? 'Evening' : 'Late night';
  return `${part} session`;
}

export function readingTime(words: number): number {
  return Math.max(1, Math.round(words / 200));
}

export interface DayGroup {
  key: string;
  ts: number;
  sweeps: Sweep[];
  tabs: number;
}

/** Groups sweeps into calendar days, newest day first, newest sweep first. */
export function groupByDay(sweeps: Sweep[]): DayGroup[] {
  const map = new Map<string, DayGroup>();
  for (const s of sweeps) {
    const k = dayKey(s.clientCreatedAt);
    const g = map.get(k);
    if (g) {
      g.sweeps.push(s);
      g.tabs += s.tabCount;
    } else {
      map.set(k, { key: k, ts: s.clientCreatedAt, sweeps: [s], tabs: s.tabCount });
    }
  }
  const groups = Array.from(map.values());
  groups.sort((a, b) => b.ts - a.ts);
  for (const g of groups) g.sweeps.sort((a, b) => b.clientCreatedAt - a.clientCreatedAt);
  return groups;
}

export interface WeekBar {
  label: string;
  count: number;
  today: boolean;
}

/** Sweep counts for the trailing 7 calendar days (oldest → today). */
export function sweepsThisWeek(sweeps: Sweep[]): WeekBar[] {
  const now = new Date();
  const bars: WeekBar[] = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(now.getDate() - i);
    const k = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    const count = sweeps.reduce((n, s) => (dayKey(s.clientCreatedAt) === k ? n + 1 : n), 0);
    bars.push({ label: WD_INITIAL[d.getDay()] ?? '', count, today: i === 0 });
  }
  return bars;
}
